import { Component, NgZone, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpService } from 'app/services/http.service';
import { ConfirmationService, MessageService } from 'primeng/api';

/**
 * Administración de las versiones de las apps (colección ConfiguracionApps).
 *
 * Reemplaza el trabajo de entrar por SSH a editar los config.json de cada paquete
 * (y el VersionInfo de variables.json, en el caso de la web de clientes). De paso
 * resuelve el problema que originó todo esto: editar el JSON en el terminal
 * corrompía las tildes y el guardia terminaba viendo "Actualizaci   n importante".
 *
 * Las tres apps móviles reciben su bloque en el endpoint que llaman en cada resume;
 * la web de clientes lo recibe dentro del JWT del login.
 */
@Component({
  selector: 'app-versionesapps',
  templateUrl: './versionesapps.component.html',
  styleUrls: ['./versionesapps.component.scss']
})
export class VersionesappsComponent implements OnInit {

  configuraciones: any[] = [];
  vigente: any = null;
  muestraVigente: boolean = false;

  modoCreaEdita: boolean = false;
  estaEditando: boolean = false;
  guardando: boolean = false;
  cargando: boolean = false;

  formulario: FormGroup;
  idEnEdicion: string = '';
  appEnEdicion: string = '';

  // El backend valida contra esta misma lista (APPS_VALIDAS en configuracionAppsRoutes.js).
  apps: any[] = [
    { label: 'Control Guard (rondas)', value: 'CG' },
    { label: 'Control de Accesos', value: 'Accesos' },
    { label: 'CG Supervisor', value: 'Supervisor' },
    { label: 'Botón de Pánico (vecinos)', value: 'PanicoComunidad' },
    { label: 'Web de clientes (dashboard)', value: 'WebClientes' }
  ];

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private formBuilder: FormBuilder,
    private http: HttpService,
    private ngZone: NgZone,
  ) {
    this.formulario = this.formBuilder.group({
      App: ['', Validators.required],
      Plataforma: [''],
      Version: ['', Validators.required],
      Build: [0],
      Mensaje: [''],
      FechaPublicacion: [''],
      VersionAppMinima: [''],
      BuildMinimo: [0],
      Habilitado: [true]
    });
  }

  async ngOnInit(): Promise<void> {
    await this.getConfiguraciones();
  }

  async getConfiguraciones(): Promise<void> {
    this.cargando = true;

    const respuesta = await this.http.getConfiguracionAppsService();
    this.configuraciones = (respuesta && respuesta.configuraciones) ? respuesta.configuraciones : [];

    this.cargando = false;
    this.ngZone.run(() => { });
  }

  async verVigente(): Promise<void> {
    const respuesta = await this.http.getConfiguracionAppsVigenteService();
    this.vigente = (respuesta && respuesta.vigente) ? respuesta.vigente : null;
    this.muestraVigente = true;
    this.ngZone.run(() => { });
  }

  // El template consulta la app seleccionada en varios *ngIf. Se expone como getter
  // (y no con getRawValue()) para no armar un objeto nuevo en cada ciclo de
  // detección de cambios.
  get appActual(): string {
    return this.formulario.get('App').value || '';
  }

  get versionActual(): string {
    return this.formulario.get('Version').value || '';
  }

  nombreApp(app: string): string {
    const item = this.apps.find(e => e.value == app);
    return item ? item.label : app;
  }

  // La web de clientes no tiene binario ni build, y sí tiene fecha de publicación:
  // el formulario se acomoda para no pedir datos que no existen.
  esWeb(app: string): boolean {
    return app == 'WebClientes';
  }

  /**
   * La app de vecinos del botón de pánico es la ÚNICA donde la versión mínima NO
   * bloquea, y el aviso de esta pantalla tiene que decirlo: si dijera "no podrán
   * operar" —que es lo que pasa con CG— nadie se atrevería a poner un mínimo por
   * miedo a dejar botones de emergencia inservibles, que es justo lo que ese
   * producto no permite.
   */
  esPanico(app: string): boolean {
    return app == 'PanicoComunidad';
  }

  /**
   * Mismo criterio laxo que usa el backend (util/versionGate.js y el CRUD): partes
   * numéricas separadas por punto. Se valida acá también para no mandar un formato
   * que el gate no sepa comparar.
   */
  versionValida(v: string): boolean {
    if (typeof v != 'string') return false;

    const partes = v.trim().split('.');
    if (partes.length == 0 || partes.length > 4) return false;

    return partes.every(p => p !== '' && /^\d+$/.test(p));
  }

  nuevo() {
    this.estaEditando = false;
    this.modoCreaEdita = true;
    this.idEnEdicion = '';
    this.appEnEdicion = '';

    this.formulario.reset({
      App: '',
      Plataforma: '',
      Version: '',
      Build: 0,
      Mensaje: '',
      FechaPublicacion: '',
      VersionAppMinima: '',
      BuildMinimo: 0,
      Habilitado: true
    });

    this.formulario.get('App').enable();
    this.formulario.get('Plataforma').enable();
  }

  editar(item: any) {
    this.estaEditando = true;
    this.modoCreaEdita = true;
    this.idEnEdicion = item._id;
    this.appEnEdicion = item.App;

    this.formulario.patchValue({
      App: item.App,
      Plataforma: item.Plataforma || '',
      Version: item.Version,
      Build: item.Build || 0,
      Mensaje: item.Mensaje || '',
      FechaPublicacion: item.FechaPublicacion || '',
      VersionAppMinima: item.VersionAppMinima || '',
      BuildMinimo: item.BuildMinimo || 0,
      Habilitado: item.Habilitado
    });

    // App y Plataforma identifican el registro (índice único): moverlos sería crear
    // otro registro, no editar este. El backend tampoco los acepta en el PUT.
    this.formulario.get('App').disable();
    this.formulario.get('Plataforma').disable();
  }

  cancelar() {
    this.modoCreaEdita = false;
    this.estaEditando = false;
    this.idEnEdicion = '';
    this.appEnEdicion = '';
    this.formulario.reset();
  }

  guardar(event: Event) {
    // Guardia de reentrada: el [loading] de PrimeNG 11 solo pinta el botón como
    // deshabilitado (agrega la clase p-disabled), no lo bloquea. Sin esto, dos clicks
    // seguidos disparan dos guardados.
    if (this.guardando) return;

    const valor = this.formulario.getRawValue();

    if (!this.versionValida(valor.Version)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Versión inválida',
        detail: 'Debe ser numérica por partes, por ejemplo 2.0.1'
      });
      return;
    }

    if (valor.VersionAppMinima && !this.versionValida(valor.VersionAppMinima)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Versión mínima inválida',
        detail: 'Debe ser numérica por partes, o quedar vacía para no exigir mínimo'
      });
      return;
    }

    if (!this.estaEditando) {
      this.crear(valor);
      return;
    }

    // Encender o subir el mínimo es la única acción de esta pantalla que puede dejar
    // gente sin poder trabajar. Se pide confirmación explícita, nombrando el valor.
    const anterior = this.configuraciones.find(e => e._id == this.idEnEdicion);
    const minimaAntes = (anterior && anterior.VersionAppMinima) ? anterior.VersionAppMinima : '';
    const minimaAhora = valor.VersionAppMinima || '';

    if (minimaAhora != minimaAntes && minimaAhora != '') {
      const detalle = this.esWeb(valor.App)
        ? `Se exigirá la versión ${minimaAhora} o superior en la web de clientes.`
        : `Los equipos con una versión anterior a ${minimaAhora} quedarán bloqueados.`;

      this.confirmationService.confirm({
        target: event.target,
        message: detalle + ' ¿Confirma?',
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: 'No',
        acceptLabel: 'Sí, aplicar',
        accept: () => { this.actualizar(valor); },
        reject: () => { return; }
      });

      return;
    }

    this.actualizar(valor);
  }

  private crear(valor: any) {
    this.guardando = true;

    const nuevo: any = {
      App: valor.App,
      Plataforma: valor.Plataforma ? valor.Plataforma : null,
      Version: valor.Version,
      Build: valor.Build || 0,
      Mensaje: valor.Mensaje || '',
      FechaPublicacion: valor.FechaPublicacion || ''
    };

    this.http.newConfiguracionAppService(nuevo).subscribe((data: any) => {
      // Todo lo que toca la vista va dentro de ngZone.run: el proyecto compila a
      // esnext, así que los await quedan nativos, zone.js no los intercepta y lo que
      // corre después de uno no repinta hasta el siguiente evento del usuario. Sin
      // esto el toast recién aparecía al hacer otro click.
      this.ngZone.run(() => {
        this.guardando = false;

        this.messageService.add({
          severity: 'success',
          summary: 'Listo!',
          detail: 'Configuración creada'
        });

        this.cancelar();
      });

      this.getConfiguraciones();
    }, err => {
      this.ngZone.run(() => {
        this.guardando = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error!',
          detail: (err.error && err.error.error) ? err.error.error : 'No se pudo crear'
        });
      });
    });
  }

  private actualizar(valor: any) {
    this.guardando = true;

    // VersionAppMinima vacío se manda como '' a propósito: es lo que APAGA el gate
    // en el backend. Omitir el campo dejaría el valor anterior intacto.
    const cambios: any = {
      Version: valor.Version,
      Build: valor.Build || 0,
      Mensaje: valor.Mensaje || '',
      FechaPublicacion: valor.FechaPublicacion || '',
      VersionAppMinima: valor.VersionAppMinima || '',
      BuildMinimo: valor.BuildMinimo || 0,
      Habilitado: valor.Habilitado
    };

    this.http.updateConfiguracionAppService(this.idEnEdicion, cambios).subscribe((data: any) => {
      this.ngZone.run(() => {
        this.guardando = false;

        this.messageService.add({
          severity: 'success',
          summary: 'Listo!',
          detail: 'Versión publicada. Las apps la verán dentro del próximo minuto.'
        });

        this.cancelar();
      });

      this.getConfiguraciones();
    }, err => {
      this.ngZone.run(() => {
        this.guardando = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error!',
          detail: (err.error && err.error.error) ? err.error.error : 'No se pudo guardar'
        });
      });
    });
  }

}
